import { useEffect, useRef, useState } from 'react';

import { faSparkles } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { faPause, faPlay, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@repo/videa-components/src/index';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { createDebugLogger } from '@videa/utils';

import {
  isRecordingSessionActiveAtom,
  isRecordingSessionPausedAtom,
  mediaBlobUrlAtom,
  monitoringStreamAtom,
  previewAudioStreamAtom,
  uploadedAudioFileAtom,
} from '../../atoms/recording';
import {
  isCancelRecordingConfirmationShownAtom,
  isRecorderSheetOpenAtom,
  shouldReopenRecorderSheetAtom,
} from '../../atoms/ui';
import { useMicrophoneContext } from '../../contexts/MicrophoneContext';
import { buttonStyling } from '../../styles';
import type { MicrophonePermissionOutcome } from '../permissions/MicrophonePermissionFlow';
import { MicrophonePermissionFlow } from '../permissions/MicrophonePermissionFlow';
import { TimerDisplay } from '../TimerDisplay';
import { UserInstructions } from '../UserInstructions';

import { getAudioRecorder } from './audioRecorderControls';
import { ConsentFlow } from './ConsentFlow';
import { VoiceCard } from './VoiceCard';

const debug = createDebugLogger('AudioRecorder');

interface AudioRecorderProps {
  onCancelRecording: () => void;
  onCancelRecordingConfirmation: () => void;
  onPauseRecording: () => void;
  onResumeRecording: () => void;
  onStartRecording: () => void;
  onStartProviderOnlyRecording: () => void;
  onGenerateNote: (uploadedFile?: File, blobUrl?: string) => void;
  onPatientConsentDenied: () => void;
  onPermissionOutcome?: (outcome: MicrophonePermissionOutcome) => void;
  isTemplateSelected: boolean;
  // Dependencies injected from consuming app
  patientName?: string;
  patientId?: string;
  enableVoiceNotesConsent?: boolean;
  // Optional styling props for extension customization
  microphonePermissionFlowProps?: {
    textClassName?: string;
    buttonClassName?: string;
    containerClassName?: string;
  };
  voiceCardProps?: {
    iconSize?: 'sm' | 'md' | 'lg';
  };
  isVoiceNotesConsentEnabled?: boolean;
  isAudioUploadEnabled?: boolean;
  showDevTools?: boolean;
  compactMode?: boolean; // Stack consent buttons vertically for narrow UIs
  templateName?: string | null;
  templateUserInstructions?: string | null;
}

export const AudioRecorder = ({
  onCancelRecording,
  onCancelRecordingConfirmation,
  onPauseRecording,
  onResumeRecording,
  onStartRecording,
  onStartProviderOnlyRecording,
  onGenerateNote,
  onPatientConsentDenied,
  onPermissionOutcome,
  isTemplateSelected,
  microphonePermissionFlowProps,
  voiceCardProps,
  isVoiceNotesConsentEnabled = false,
  isAudioUploadEnabled = false,
  showDevTools = false,
  compactMode = false,
  templateName,
  templateUserInstructions,
}: AudioRecorderProps) => {
  // atoms
  const isRecordingSessionActive = useAtomValue(isRecordingSessionActiveAtom);
  const isRecordingSessionPaused = useAtomValue(isRecordingSessionPausedAtom);
  const mediaBlobUrl = useAtomValue(mediaBlobUrlAtom);
  const setMediaBlobUrl = useSetAtom(mediaBlobUrlAtom);
  const setUploadedAudioFile = useSetAtom(uploadedAudioFileAtom);
  const previewAudioStream = useAtomValue(previewAudioStreamAtom);
  const [isSheetOpen, setIsSheetOpen] = useAtom(isRecorderSheetOpenAtom);
  const [shouldReopen, setShouldReopen] = useAtom(
    shouldReopenRecorderSheetAtom,
  );
  const [
    isCancelRecordingConfirmationShown,
    setIsCancelRecordingConfirmationShown,
  ] = useAtom(isCancelRecordingConfirmationShownAtom);
  const monitoringStream = useAtomValue(monitoringStreamAtom);
  // Permission state comes from MicrophoneContext (browser API source of truth)
  const { hasPermission: hasMicrophonePermission } = useMicrophoneContext();

  // audio recording control interface
  const startRecording = () => getAudioRecorder().start?.();
  const stopRecording = () => getAudioRecorder().stop?.();
  const pauseRecording = () => getAudioRecorder().pause?.();
  const resumeRecording = () => getAudioRecorder().resume?.();
  const clearRecording = (deleteOpfsSession = true) =>
    getAudioRecorder().clear?.(deleteOpfsSession);

  // local state
  const [isRecordingStopped, setIsRecordingStopped] = useState(false);
  const [isRecordingCanceled, setIsRecordingCanceled] = useState(false);

  const displayNoticeText = () => {
    if (!isRecordingSessionActive) return null;
    if (isCancelRecordingConfirmationShown) {
      return 'Recording paused - waiting for confirmation to cancel.';
    }
    if (isRecordingSessionPaused) {
      return 'Audio capture is paused.';
    }
    return 'Capturing audio...';
  };

  const displayNoticeDetailsText = () => {
    if (!isRecordingSessionActive) return null;
    if (isCancelRecordingConfirmationShown) {
      return "If you cancel now, you'll lose all progress on this recording.";
    }
    return "When you're done speaking, select Generate Note.";
  };

  const handleStartRecording = () => {
    try {
      startRecording();
      onStartRecording();
    } catch (err) {
      debug.error('Failed to start recording:', err);
    }
  };

  const handleStartProviderOnlyRecording = () => {
    try {
      startRecording();
      onStartProviderOnlyRecording();
    } catch (err) {
      debug.error('Failed to start recording:', err);
    }
  };

  /**
   * This pauses the active recording session, and toggles on
   * the "cancel confirmation" button set in place of the default
   * audio recorder controls.
   */
  const handleCancelButtonClick = () => {
    pauseRecording();
    onCancelRecording();
    setIsCancelRecordingConfirmationShown(true);
  };

  /**
   * This is effective the "go back" action if a user does not want
   * to commit to actually canceling the active recording.
   */
  const handleNotNowButtonClick = () => {
    resumeRecording();
    setIsCancelRecordingConfirmationShown(false);
  };

  /**
   * This stops recording audio via MediaStream Recording API.
   * As soon as `stopRecording()` finishes running, the value of `mediaBlobUrl`
   * should no longer be `undefined`, which will bubble back up to the parent.
   */
  const handleStopRecording = () => {
    setIsRecordingStopped(true);
    setIsRecordingCanceled(false);
    stopRecording();
  };

  /**
   * This is the "cancel confirmation" action, which stops the active
   * recording, fires off an async request to clear the mediaBlobUrl,
   * hides the "cancel confirm" button set and invokes the onCancelRecording
   * callback.
   */
  const handleCancelRecordingConfirmationButtonClick = () => {
    setIsRecordingStopped(true);
    setIsRecordingCanceled(true);
    setIsCancelRecordingConfirmationShown(false);
    stopRecording();
    if (!isSheetOpen) {
      setShouldReopen(true);
    }
  };

  const handleConsentDeniedClick = () => {
    setIsSheetOpen(false);
    onPatientConsentDenied();
  };

  /**
   * Handles audio file upload. Sets the blob URL and uploaded file,
   * then triggers note generation.
   */
  const handleAudioFileSelected = (file: File, blobUrl: string) => {
    // Store the uploaded file so it can be used directly during upload
    setUploadedAudioFile(file);
    // Set the blob URL to trigger the note generation flow
    setMediaBlobUrl(blobUrl);
    // Pass file and blobUrl directly to avoid closure issues
    // where handleSave would see stale atom values
    onGenerateNote(file, blobUrl);
  };

  // store the latest version of onGenerateNote in a ref
  const onGenerateNoteRef = useRef(onGenerateNote);
  const onCancelRecordingRef = useRef(onCancelRecording);
  const onCancelRecordingConfirmationRef = useRef(
    onCancelRecordingConfirmation,
  );

  useEffect(() => {
    onGenerateNoteRef.current = onGenerateNote;
  }, [onGenerateNote]);

  useEffect(() => {
    onCancelRecordingRef.current = onCancelRecording;
  }, [onCancelRecording]);

  useEffect(() => {
    onCancelRecordingConfirmationRef.current = onCancelRecordingConfirmation;
  }, [onCancelRecordingConfirmation]);

  useEffect(() => {
    if (shouldReopen) {
      setIsSheetOpen(true);
      setShouldReopen(false);
    }
  }, [shouldReopen, setIsSheetOpen, setShouldReopen]);

  useEffect(() => {
    if (!isRecordingStopped) return;

    if (isRecordingCanceled) {
      if (mediaBlobUrl !== undefined) {
        clearRecording(true); // Delete OPFS session on cancel
        onCancelRecordingConfirmationRef.current();
        setIsRecordingStopped(false);
        setIsRecordingCanceled(false);
      }
    } else if (mediaBlobUrl !== undefined) {
      onGenerateNoteRef.current();
      // Don't delete OPFS session here - handleRecordingComplete may still need it
      // for blob reconstruction. Session cleanup happens after successful upload.
      clearRecording(false);
      setIsRecordingStopped(false);
    }
  }, [isRecordingStopped, isRecordingCanceled, mediaBlobUrl]);

  return (
    <div
      className={`vai-flex vai-flex-col vai-items-center vai-w-full ${isRecordingSessionActive && isSheetOpen ? 'vai-h-full vai-min-h-0' : 'vai-space-y-4'}`}
    >
      {!hasMicrophonePermission && (
        <MicrophonePermissionFlow
          {...microphonePermissionFlowProps}
          onAudioFileSelected={handleAudioFileSelected}
          isAudioUploadEnabled={isAudioUploadEnabled}
          showDevTools={showDevTools}
          onPermissionOutcome={onPermissionOutcome}
        />
      )}

      {hasMicrophonePermission && (
        <>
          {/* Recorder visualizer - fixed at top */}
          <div className="vai-flex vai-items-center vai-justify-center vai-flex-shrink-0">
            <div
              className={`vai-flex vai-items-center vai-gap-1 vai-px-4 vai-py-4 ${isRecordingSessionActive && isSheetOpen ? 'vai-w-full' : ''}`}
            >
              {/* Timer Display - only show when recording */}
              {isRecordingSessionActive && isSheetOpen && (
                <div className="vai-w-1/2 vai-flex vai-justify-center vai-pr-4">
                  <TimerDisplay />
                </div>
              )}
              {/* Microphone Visualizer */}
              <div className="vai-flex vai-items-center">
                <VoiceCard
                  stream={monitoringStream || previewAudioStream}
                  isRecordingSessionActive={isRecordingSessionActive}
                  {...voiceCardProps}
                />
              </div>
            </div>
          </div>

          {/* Flex container for notice, instructions, and buttons when recording */}
          {isRecordingSessionActive && isSheetOpen ? (
            <div className="vai-flex vai-flex-col vai-flex-1 vai-min-h-0 vai-w-full vai-items-center vai-gap-6">
              {/* Notice text - fixed above instructions */}
              <div className="vai-flex vai-flex-col vai-items-center vai-flex-shrink-0">
                <p className="vai-text-xl vai-font-bold">
                  {displayNoticeText()}
                </p>
                <p className="vai-text-sm vai-text-muted-foreground vai-pt-2">
                  {displayNoticeDetailsText()}
                </p>
              </div>

              {/* User instructions for procedure templates - scrollable in middle */}
              {templateUserInstructions && templateName && (
                <UserInstructions
                  templateName={templateName}
                  userInstructions={templateUserInstructions}
                />
              )}

              {/* Buttons - fixed at bottom */}
              <div className="vai-flex-shrink-0 vai-mt-auto">
                <div className="vai-flex vai-space-x-3">
                  {!isCancelRecordingConfirmationShown && (
                    <>
                      <Button
                        variant="ghost"
                        onClick={handleCancelButtonClick}
                        size={isSheetOpen ? 'default' : 'sm'}
                        disabled={isCancelRecordingConfirmationShown}
                        className="!vai-text-slate-700 hover:!vai-bg-slate-100"
                      >
                        <FontAwesomeIcon icon={faXmark} />
                        Cancel
                      </Button>

                      {isRecordingSessionPaused ? (
                        <Button
                          variant="sheet"
                          onClick={() => {
                            resumeRecording();
                            onResumeRecording();
                          }}
                          className="hover:vai-ring-slate-200/50 !vai-text-slate-700 !vai-bg-white hover:!vai-bg-slate-50 vai-border vai-border-slate-300"
                          size={isSheetOpen ? 'default' : 'sm'}
                        >
                          <FontAwesomeIcon icon={faPlay} />
                          Resume
                        </Button>
                      ) : (
                        <Button
                          variant="sheet"
                          onClick={() => {
                            pauseRecording();
                            onPauseRecording();
                          }}
                          className={`${buttonStyling} hover:vai-ring-slate-200/50 !vai-text-slate-700 !vai-bg-white hover:!vai-bg-slate-50 vai-border vai-border-slate-300`}
                          size={isSheetOpen ? 'default' : 'sm'}
                        >
                          <FontAwesomeIcon icon={faPause} />
                          Pause
                        </Button>
                      )}
                      <Button
                        onClick={handleStopRecording}
                        variant={'pulse'}
                        size={isSheetOpen ? 'default' : 'sm'}
                        className="!vai-text-white !vai-bg-blue-600 hover:!vai-bg-blue-700 vai-border vai-border-blue-700"
                      >
                        <span>
                          <FontAwesomeIcon icon={faSparkles} />
                        </span>
                        Generate Note
                      </Button>
                    </>
                  )}

                  {/* Cancel confirmation appears at bottom when triggered */}
                  {isCancelRecordingConfirmationShown && (
                    <>
                      <Button
                        variant="destructive"
                        onClick={handleCancelRecordingConfirmationButtonClick}
                        className="vai-px-6 !vai-bg-red-600 hover:!vai-bg-red-700 !vai-text-white"
                      >
                        Cancel Recording
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleNotNowButtonClick}
                        className="vai-px-6 !vai-text-slate-700 !vai-bg-white vai-border vai-border-slate-300"
                      >
                        Not Now
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div>
              {!isRecordingSessionActive ? (
                <ConsentFlow
                  onConsentDenied={handleConsentDeniedClick}
                  onStartProviderOnlyRecording={
                    handleStartProviderOnlyRecording
                  }
                  onStartRecording={handleStartRecording}
                  onAudioFileSelected={handleAudioFileSelected}
                  isTemplateSelected={isTemplateSelected}
                  monitoringStream={monitoringStream}
                  mediaBlobUrl={mediaBlobUrl}
                  isSheetOpen={isSheetOpen}
                  buttonStyling={buttonStyling}
                  isVoiceNotesConsentEnabled={isVoiceNotesConsentEnabled}
                  isAudioUploadEnabled={isAudioUploadEnabled}
                  showDevTools={showDevTools}
                  compactMode={compactMode}
                />
              ) : null}
            </div>
          )}
        </>
      )}
    </div>
  );
};
